import { BrandMark } from '../components/BrandMark';
import React, { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

export default function Login() {
  const app = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@kyc.local');
  const [forgotAvailable, setForgotAvailable] = useState(false);
  const [forgot, setForgot] = useState(null);
  useEffect(() => { api.get('/auth/forgot/available').then(({ data }) => setForgotAvailable(!!data.available)).catch(() => {}); }, []);
  const sendReset = async (e) => {
    e.preventDefault();
    setForgot({ ...forgot, busy: true });
    try { await api.post('/auth/forgot', { email: forgot.email }); setForgot({ ...forgot, busy: false, sent: true }); }
    catch (err) { toast.error(errorMessage(err)); setForgot({ ...forgot, busy: false }); }
  };
  const [password, setPassword] = useState('Admin@123');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    app.login(email, password).then((res) => {
      setBusy(false);
      if (!res.ok) setError(res.error);
      else navigate('/dashboard');
    });
  };

  return (
    <div className="min-h-screen bg-paper-100 grid lg:grid-cols-[1.1fr_1fr]">
      <div className="hidden lg:flex flex-col justify-between p-12 border-r border-line bg-paper-50 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative"><BrandMark size="md" /></div>
        <div className="relative max-w-lg">
          <h1 className="text-[44px] leading-[1.05] font-semibold tracking-tight text-ink-800">Deterministic, auditable KYC compliance.</h1>
          <p className="mt-5 text-[17px] text-ink-500 leading-relaxed">Configure once, collect once, reuse safely. Every status resolves to the exact rule version, evidence and decision that produced it.</p>
          <div className="mt-10 grid grid-cols-3 gap-4">
            {[['Effective-dated', 'rule sets'], ['Cross-class', 'evidence reuse'], ['Item-level', 'explainability']].map(([a, b]) => (
              <div key={a} className="rounded-xl border border-line bg-white p-4 shadow-card">
                <div className="text-sm font-semibold text-ink-800">{a}</div>
                <div className="text-xs text-ink-400 mt-0.5">{b}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-ink-400">Self-hosted • Role-based access • Immutable audit history</div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-12">
        <form onSubmit={submit} className="w-full max-w-sm">
          <div className="lg:hidden mb-8"><BrandMark size="md" /></div>
          <h2 className="text-2xl font-semibold tracking-tight text-ink-800">Sign in</h2>
          <p className="text-sm text-ink-500 mt-1">Internal users only. External submitters use their secure link.</p>
          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-10 bg-white" autoComplete="username" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-10 bg-white" autoComplete="current-password" />
            </div>
            {error && <div data-testid="login-error" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
            {forgotAvailable && !forgot && <div className="text-right -mt-1"><button type="button" data-testid="forgot-link" onClick={() => setForgot({ email, busy: false, sent: false })} className="text-xs text-ink-500 hover:text-ink-800 underline underline-offset-2">Forgot password?</button></div>}
            {forgot && (
              <div data-testid="forgot-panel" className="rounded-xl border border-line bg-white p-4 space-y-3">
                {forgot.sent ? <div data-testid="forgot-sent" className="text-sm text-ink-700">If an account exists for <span className="font-medium">{forgot.email}</span>, we've emailed a reset link. It is valid for one hour.</div> : <>
                  <div className="text-sm font-medium text-ink-800">Reset your password</div>
                  <Input data-testid="forgot-email" type="email" value={forgot.email} onChange={(e) => setForgot({ ...forgot, email: e.target.value })} className="h-9 bg-white" placeholder="you@company.com" />
                  <div className="flex gap-2"><Button type="button" data-testid="forgot-submit" onClick={sendReset} disabled={forgot.busy || !forgot.email} className="h-9 bg-ink-700 hover:bg-ink-800 text-white">{forgot.busy ? 'Sending…' : 'Email me a reset link'}</Button><Button type="button" variant="ghost" className="h-9" onClick={() => setForgot(null)}>Cancel</Button></div>
                </>}
                {forgot.sent && <button type="button" className="text-xs text-ink-500 underline underline-offset-2" onClick={() => setForgot(null)}>Back to sign in</button>}
              </div>
            )}
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-10 bg-ink-700 hover:bg-ink-800 text-white">{busy ? 'Signing in…' : 'Sign in'} <ArrowRight className="h-4 w-4 ml-1.5" /></Button>
          </div>
          <div className="mt-8 rounded-xl border border-line bg-white p-4">
            <div className="flex items-center gap-2 text-xs font-semibold text-ink-500 uppercase tracking-wider"><Lock className="h-3.5 w-3.5" /> Demo accounts</div>
            <div className="mt-3 grid gap-1.5 text-[13px]">
              {[['admin@kyc.local', 'Admin@123', 'System admin (temp)'], ['priya@kyc.local', 'Review@123', 'Reviewer'], ['marcus@kyc.local', 'Senior@123', 'Senior approver'], ['elena@kyc.local', 'Config@123', 'Config admin']].map(([e, p, r]) => (
                <button type="button" key={e} onClick={() => { setEmail(e); setPassword(p); }} className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-paper-100 text-left transition-colors">
                  <span className="font-mono text-ink-700">{e}</span><span className="text-ink-400">{r}</span>
                </button>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
